import { Info, X } from 'lucide-react-native';
import { useEffect } from 'react';
import { Modal, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';

import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

interface WhyContent {
  title: string;
  why: string | null;
  tier: string | null;
}

/**
 * Long-press bubble: shows a task's title + "why it matters" copy without
 * navigating to the detail screen. The "coach not vault" wedge — the user
 * gets the reason in two seconds, dismisses, and keeps moving.
 */
export function WhyPopover({
  content,
  onClose,
}: {
  content: WhyContent | null;
  onClose: () => void;
}) {
  const insets = useSafeAreaInsets();
  const open = content != null;
  const anim = useSharedValue(0);

  useEffect(() => {
    anim.value = withTiming(open ? 1 : 0, {
      duration: 220,
      easing: Easing.out(Easing.cubic),
    });
  }, [open, anim]);

  const sheetStyle = useAnimatedStyle(() => ({
    opacity: anim.value,
    transform: [{ translateY: (1 - anim.value) * 40 }],
  }));
  const backdropStyle = useAnimatedStyle(() => ({ opacity: anim.value * 0.55 }));

  if (!content) return null;

  return (
    <Modal animationType="none" transparent visible={open} onRequestClose={onClose}>
      <Animated.View style={[styles.backdrop, backdropStyle]}>
        <Pressable style={styles.backdropPress} onPress={onClose} />
      </Animated.View>
      <View pointerEvents="box-none" style={styles.wrap}>
        <Animated.View style={[styles.sheet, { paddingBottom: 20 + insets.bottom }, sheetStyle]}>
          <View style={styles.head}>
            <View style={styles.iconBubble}>
              <Info size={18} color={Palette.honeyDeep} strokeWidth={1.8} />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.eyebrow}>WHY IT MATTERS</Text>
              <Text style={styles.title} numberOfLines={3}>{content.title}</Text>
            </View>
            <Pressable
              onPress={onClose}
              hitSlop={10}
              style={({ pressed }) => [styles.closeBtn, pressed && { opacity: 0.7 }]}>
              <X size={18} color={Palette.ink} strokeWidth={2} />
            </Pressable>
          </View>
          <ScrollView style={{ maxHeight: 260 }} contentContainerStyle={styles.body}>
            <Text style={styles.why}>
              {content.why ?? "No 'why' written for this one yet. Add it from the task detail screen so future-you remembers the point."}
            </Text>
          </ScrollView>
        </Animated.View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(15, 24, 18, 1)',
  },
  backdropPress: { flex: 1 },
  wrap: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  sheet: {
    width: '100%',
    maxWidth: 440,
    backgroundColor: Palette.sand,
    borderTopLeftRadius: 28,
    borderTopRightRadius: 28,
    paddingHorizontal: 20,
    paddingTop: 18,
    paddingBottom: 28,
  },
  head: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 6,
  },
  iconBubble: {
    width: 36, height: 36, borderRadius: 11,
    backgroundColor: 'rgba(225,163,61,0.18)',
    alignItems: 'center', justifyContent: 'center',
  },
  eyebrow: {
    fontFamily: Fonts.sansBold,
    fontSize: 11,
    color: Palette.honeyDeep,
    letterSpacing: 0.8,
  },
  title: {
    fontFamily: Fonts.serifBold,
    fontSize: 17,
    color: Palette.ink,
    letterSpacing: -0.3,
    marginTop: 2,
  },
  closeBtn: {
    width: 32, height: 32, borderRadius: 16,
    backgroundColor: Palette.card,
    borderWidth: 1, borderColor: Palette.line,
    alignItems: 'center', justifyContent: 'center',
  },
  body: {
    paddingTop: 8,
  },
  why: {
    fontFamily: Fonts.sans,
    fontSize: 14,
    lineHeight: 21,
    color: Palette.inkSoft,
  },
});
